import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { generateDataDogContext } from '../../utils/dataDog'
import { castIProducts } from '../../utils/castICatalogItems'
import {
  getFlattedCartItems,
  setProductsQuantity,
} from '../../utils/utilsForCatalogReducer'
import {
  ICartGroup,
  ICartProduct,
  IConfig,
  IProduct,
} from '../../interfaces'
import { ActivePromotion, IProductCatalog } from '../../graphQL/commons'

interface RecommendationCartContext {
  items: ICartProduct[]
  groups: ICartGroup[]
  activePromotions: ActivePromotion[]
}

// Re-fetches must reapply cart quantities — otherwise dispatching raw
// response items overwrites a cart-merged slice and counters reset to 0.
export const mergeCartQuantitiesIntoRecommendationItems = (
  responseItems: IProduct[],
  cart: RecommendationCartContext,
  config: IConfig | undefined,
): IProduct[] => {
  // Guard against malformed add-on responses (null / non-array items)
  // before they reach `.map` inside castIProducts.
  const safeItems = Array.isArray(responseItems) ? responseItems : []
  const { flattedCartItems } = getFlattedCartItems({
    items: cart.items,
    groups: cart.groups,
  })
  // Response is typed IProduct[] but carries the IProductCatalog shape
  // (jumpQty, layerQty…) per the GraphQL fragment.
  const products = castIProducts(
    safeItems as unknown as IProductCatalog[],
    config,
    false,
  )
  return setProductsQuantity({
    products,
    flattedCartItems,
    config,
    activePromotions: cart.activePromotions,
    isSplitPromotionTagAllowed:
      config?.options?.splitPromotionTagRecommendedProducts,
  })
}

/**
 * Logs a recommendation-fetch error to Datadog RUM with a structured context.
 * Shared by `useBuyAgain` / `usePreviouslyPurchased` / `useRecommendedForYou`
 * for both the Apollo `onError` path and the synchronous-throw catch path.
 *
 * For ApolloErrors, lifts `graphQLErrors[].extensions.code` (the typed
 * error code from the storefront's HeadlessError — e.g. `BAD_REQUEST`,
 * `FAILED_DEPENDENCY`, `INTERNAL_SERVER`) and `networkError.message` into
 * separate facets so Datadog dashboards can split alerts by failure mode
 * without parsing the raw error.
 *
 * The hook still owns the matching `setStatus('ERROR')` dispatch so each
 * carousel writes to its own slice; this helper only centralises the
 * boilerplate that has zero per-hook variation.
 */
export const handleRecommendationFetchError = (
  exception: unknown,
  functionName: string,
  title: string,
) => {
  const extraInfo: Record<string, unknown> = { function: functionName }
  if (exception instanceof ApolloError) {
    extraInfo.graphQLErrorCodes = exception.graphQLErrors?.map(
      (e) => e.extensions?.code,
    )
    extraInfo.graphQLErrorMessages = exception.graphQLErrors?.map(
      (e) => e.message,
    )
    extraInfo.networkError = exception.networkError?.message
  }
  const context = generateDataDogContext({ title, extraInfo })
  datadogRum.startView(context.viewName)
  datadogRum.addError(exception, context)
}

/**
 * Guard at the top of each recommendation hook's fetch function. If
 * `storefrontName` or `sessionUid` are missing (e.g. the EventManager
 * fired the action before session bootstrap completed), logs to Datadog
 * and returns `true` so the caller can early-return without dispatching
 * a wasted `LOADING` state.
 *
 * Does NOT dispatch `setStatus('ERROR')` — leaving the slice in its
 * current state means a later valid call can proceed normally instead
 * of staying locked in ERROR after a startup race.
 */
export const skipIfMissingIdentifiers = (
  variables: { storefrontName?: string; sessionUid?: string },
  functionName: string,
  carouselName: string,
): boolean => {
  if (variables.storefrontName && variables.sessionUid) return false
  handleRecommendationFetchError(
    new Error('called with empty storefrontName or sessionUid'),
    functionName,
    `${carouselName} fetch skipped — missing identifiers`,
  )
  return true
}
